import { useMutation, useQueryClient } from '@tanstack/react-query';

import type {
  PatchAdminV1StoresStoreIdBlogCategoriesIdBody,
  PostAdminV1StoresStoreIdBlogCategoriesBody,
} from '@nucleus-commerce/api-client/admin';

import { blogApi } from '../api/blog-api';

import { blogKeys } from './blog-keys';

/** Posts embed their category's handle and title, so every category change refetches posts. */
function refresh(queryClient: ReturnType<typeof useQueryClient>, storeId: string) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: blogKeys.categories(storeId) }),
    queryClient.invalidateQueries({ queryKey: blogKeys.posts(storeId) }),
  ]);
}

export function useCreateBlogCategory(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PostAdminV1StoresStoreIdBlogCategoriesBody) =>
      blogApi.createCategory(storeId, body),
    meta: { successMessage: 'blog:toasts.categoryCreated', silentError: true },
    onSuccess: () => refresh(queryClient, storeId),
  });
}

export function useUpdateBlogCategory(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: PatchAdminV1StoresStoreIdBlogCategoriesIdBody;
    }) => blogApi.updateCategory(storeId, id, body),
    meta: { successMessage: 'blog:toasts.categoryUpdated', silentError: true },
    onSuccess: () => refresh(queryClient, storeId),
  });
}

export function useDeleteBlogCategory(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => blogApi.removeCategory(storeId, id),
    meta: { successMessage: 'blog:toasts.categoryDeleted', silentError: true },
    onSuccess: () => refresh(queryClient, storeId),
  });
}
